import { useState } from "react";
import { Plus, Pencil, Trash2, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DataTable,
  FormInput,
  PageHeader,
  useDataTable,
  type DataTableColumn,
} from "@/components/shared";
import {
  useAdminCmsPages,
  useCreateCmsPageMutation,
  useDeleteCmsPageMutation,
  useUpdateCmsPageMutation,
} from "@/hooks/queries";
import type { CmsPageDto } from "@/types/cms.dto";
import { getApiErrorMessage } from "@/services/httpClient";
import { useToast } from "@/hooks/use-toast";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "react-router-dom";

const emptyForm = {
  slug: "",
  title: "",
  subtitle: "",
  contentHtml: "<p>Page content</p>",
  ctaLabel: "",
  ctaUrl: "",
  displayOrder: 1,
  isPublished: true,
};

const AdminCmsPages = () => {
  const { toast } = useToast();
  const { data, isLoading } = useAdminCmsPages();
  const createMutation = useCreateCmsPageMutation();
  const updateMutation = useUpdateCmsPageMutation();
  const deleteMutation = useDeleteCmsPageMutation();
  const pages = data?.items ?? [];
  const [sheetOpen, setSheetOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [editing, setEditing] = useState<CmsPageDto | null>(null);
  const [form, setForm] = useState(emptyForm);

  const table = useDataTable<CmsPageDto>({
    data: pages,
    searchKeys: ["slug", "title"],
  });

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, displayOrder: pages.length + 1 });
    setSheetOpen(true);
  };

  const openEdit = (page: CmsPageDto) => {
    setEditing(page);
    setForm({
      slug: page.slug,
      title: page.title,
      subtitle: page.subtitle ?? "",
      contentHtml: page.contentHtml,
      ctaLabel: page.ctaLabel ?? "",
      ctaUrl: page.ctaUrl ?? "",
      displayOrder: page.displayOrder,
      isPublished: page.isPublished,
    });
    setSheetOpen(true);
  };

  const handleSave = async () => {
    if (!form.slug.trim() || !form.title.trim()) return;
    const dto = {
      slug: form.slug.trim().toLowerCase().replace(/\s+/g, "-"),
      title: form.title,
      subtitle: form.subtitle || undefined,
      contentHtml: form.contentHtml,
      ctaLabel: form.ctaLabel || undefined,
      ctaUrl: form.ctaUrl || undefined,
      displayOrder: form.displayOrder,
      isPublished: form.isPublished,
    };

    try {
      if (editing) {
        await updateMutation.mutateAsync({ id: editing.id, dto });
        toast({ title: "Page updated" });
      } else {
        await createMutation.mutateAsync(dto);
        toast({ title: "Page created" });
      }
      setSheetOpen(false);
    } catch (error) {
      toast({ title: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  const columns: DataTableColumn<CmsPageDto>[] = [
    { id: "slug", header: "Slug", cell: (row) => <code className="text-xs">{row.slug}</code> },
    { id: "title", header: "Title", cell: (row) => row.title },
    {
      id: "published",
      header: "Status",
      cell: (row) => (
        <Badge variant={row.isPublished ? "default" : "secondary"}>
          {row.isPublished ? "Published" : "Draft"}
        </Badge>
      ),
    },
    {
      id: "actions",
      header: "",
      align: "right",
      cell: (row) => (
        <div className="flex gap-1 justify-end">
          <Button variant="ghost" size="icon" asChild>
            <Link to={`/page/${row.slug}`} target="_blank">
              <ExternalLink className="h-4 w-4" />
            </Link>
          </Button>
          <Button variant="ghost" size="icon" onClick={() => openEdit(row)}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => setDeleteId(row.id)}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="CMS Pages"
        description="Manage static content pages (About, Help, Terms, etc.)."
        actions={
          <Button onClick={openCreate} className="gap-2">
            <Plus className="h-4 w-4" /> Add Page
          </Button>
        }
      />

      {isLoading ? (
        <Skeleton className="h-64 rounded-xl" />
      ) : (
        <DataTable
          title={`All Pages (${pages.length})`}
          columns={columns}
          data={table.rows}
          getRowId={(row) => String(row.id)}
          search={table.state.search}
          onSearchChange={table.setSearch}
          searchPlaceholder="Search pages…"
          sort={table.state.sort}
          onSortChange={table.toggleSort}
          pagination={table.pagination}
          onPageChange={table.setPage}
          onPageSizeChange={table.setPageSize}
        />
      )}

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="right" className="flex w-full flex-col gap-0 overflow-y-auto p-0 sm:max-w-xl">
          <SheetHeader className="border-b px-6 py-5 text-left">
            <SheetTitle>{editing ? "Edit Page" : "New Page"}</SheetTitle>
            <SheetDescription>
              {editing ? "Update page content and publishing settings." : "Create a new static content page."}
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
            <FormInput label="Slug *" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} disabled={!!editing} />
            <FormInput label="Title *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <FormInput label="Subtitle" value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} />
            <div className="space-y-2">
              <Label>Content (HTML)</Label>
              <Textarea rows={10} value={form.contentHtml} onChange={(e) => setForm({ ...form, contentHtml: e.target.value })} />
            </div>
            <FormInput label="CTA Label" value={form.ctaLabel} onChange={(e) => setForm({ ...form, ctaLabel: e.target.value })} />
            <FormInput label="CTA URL" value={form.ctaUrl} onChange={(e) => setForm({ ...form, ctaUrl: e.target.value })} />
            <FormInput label="Display Order" type="number" value={String(form.displayOrder)} onChange={(e) => setForm({ ...form, displayOrder: Number(e.target.value) })} />
            <div className="flex items-center justify-between rounded-lg border p-3">
              <Label>Published</Label>
              <Switch checked={form.isPublished} onCheckedChange={(v) => setForm({ ...form, isPublished: v })} />
            </div>
          </div>

          <SheetFooter className="mt-auto border-t bg-background px-6 py-4 sm:flex-row sm:justify-end sm:gap-2">
            <Button variant="outline" onClick={() => setSheetOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={createMutation.isPending || updateMutation.isPending}>Save</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <AlertDialog open={deleteId != null} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete page?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (deleteId == null) return;
                try {
                  await deleteMutation.mutateAsync(deleteId);
                  toast({ title: "Page deleted" });
                } catch (error) {
                  toast({ title: getApiErrorMessage(error), variant: "destructive" });
                }
                setDeleteId(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminCmsPages;
